import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  isAfter,
  isValid,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
} from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Box } from 'components'
import Button from 'components/Buttons/Button'
import DatePicker from 'components/DatePicker'
import PopOver from 'components/PopOver'
import { CustomMenuProps as PopOverCustomMenuType, PopoverPlacement } from 'components/PopOver/types'
import TextInput from 'components/TextInput'
import Tooltip from 'components/Tooltip'

import { WidthProps } from 'types/styled'
import { TFunction } from 'i18next'

const DATE_PICKER_WIDTH = 375

export type DateRangeOption =
  | 'all'
  | 'custom'
  | '7_days'
  | 'last_3_month'
  | 'last_6_month'
  | 'lastyear'
  | 'previousmonth'
  | 'this_month'
  | 'this_quarter'
  | 'previous_quarter'
  | 'thisyear'
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'next_7_days'

type DateFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy/dd/MM'

export type DateValue = {
  option: DateRangeOption
  dateFrom?: string | Date
  dateTo?: string | Date
}

type MenuProps = {
  dateFormat?: DateFormat
  onSubmit: (value?: DateValue) => void
  value?: DateValue | null
  options?: DateRangeOption[]
  placement?: PopoverPlacement
  trackId?: string
  /** Upper bound for the "custom" range date pickers (e.g. `new Date()` to block future dates). */
  maxDate?: Date
  umamiEvent?: string
}

type FilterProps = MenuProps & {
  title: string
  width?: WidthProps
  isPortaled?: boolean
  withUpdatedStyleProps?: boolean
}

const DATE_TODAY = new Date()

const DEFAULT_OPTIONS: MenuProps['options'] = [
  'today',
  'yesterday',
  'tomorrow',
  '7_days',
  'next_7_days',
  'this_month',
  'previousmonth',
  'thisyear',
  'lastyear',
  'custom',
  'all',
]

const isCustomOption = (option: string) => option === 'custom'

const getPopOverStyle = ({ triggerRect }: { triggerRect: DOMRect }) => {
  const dtMarginPadding = 100
  const dtPickersWidth = DATE_PICKER_WIDTH * 2 + dtMarginPadding
  const viewportWidth = window.innerWidth
  const spaceLeft = triggerRect.left
  const spaceRight = viewportWidth - triggerRect.right

  let popoverAdditionalStyle = {}

  const baseStyle = {
    maxWidth: 'fit-content',
  }

  if (viewportWidth < dtPickersWidth) {
    popoverAdditionalStyle = {
      ...baseStyle,
      left: `-${spaceLeft}px`,
    }
  } else if (spaceLeft > spaceRight) {
    popoverAdditionalStyle = {
      ...baseStyle,
      left: `${-dtPickersWidth + triggerRect.width}px`,
    }
  } else {
    if (dtPickersWidth > spaceRight) {
      popoverAdditionalStyle = {
        ...baseStyle,
        left: `-${spaceLeft}px`,
      }
    } else {
      popoverAdditionalStyle = {
        ...baseStyle,
      }
    }
  }
  return popoverAdditionalStyle
}

const resolveDateRangeValue = (dateFrom: string | Date, dateTo: string | Date, option?: DateRangeOption) => {
  if (!option) return undefined

  const result: DateValue = {
    option,
    dateFrom,
    dateTo,
  }

  let computedDateFrom, computedDateTo

  switch (option) {
    case 'custom':
      return {
        ...result,
        dateFrom: startOfDay(
          isValid(dateFrom) ? (typeof dateFrom === 'string' ? new Date(dateFrom) : dateFrom) : new Date(),
        ),
        dateTo: endOfDay(isValid(dateTo) ? (typeof dateTo === 'string' ? new Date(dateTo) : dateTo) : new Date()),
      }
    case '7_days':
      return {
        ...result,
        dateFrom: startOfDay(subDays(DATE_TODAY, 6)),
        dateTo: endOfDay(subDays(DATE_TODAY, 0)),
      }
    case 'last_3_month':
    case 'last_6_month':
      computedDateFrom = startOfMonth(subMonths(DATE_TODAY, option === 'last_3_month' ? 3 : 6))
      computedDateTo = endOfMonth(subMonths(DATE_TODAY, 1))

      return {
        ...result,
        dateFrom: computedDateFrom,
        dateTo: computedDateTo,
      }
      break
    case 'lastyear':
      const dateLastYear = subMonths(DATE_TODAY, 12)

      return {
        ...result,
        dateFrom: startOfYear(dateLastYear),
        dateTo: endOfYear(dateLastYear),
      }
    case 'previousmonth':
      computedDateFrom = startOfMonth(subMonths(DATE_TODAY, 1))
      computedDateTo = endOfMonth(subMonths(DATE_TODAY, 1))

      return {
        ...result,
        dateFrom: computedDateFrom,
        dateTo: computedDateTo,
      }
    case 'this_month':
      return {
        ...result,
        dateFrom: startOfMonth(DATE_TODAY),
        dateTo: endOfMonth(DATE_TODAY),
      }
    case 'this_quarter':
      return {
        ...result,
        dateFrom: startOfQuarter(DATE_TODAY),
        dateTo: endOfQuarter(DATE_TODAY),
      }
    case 'previous_quarter': {
      const datePreviousQuarter = subQuarters(DATE_TODAY, 1)

      return {
        ...result,
        dateFrom: startOfQuarter(datePreviousQuarter),
        dateTo: endOfQuarter(datePreviousQuarter),
      }
    }
    case 'thisyear':
      return {
        ...result,
        dateFrom: startOfYear(DATE_TODAY),
        dateTo: endOfYear(DATE_TODAY),
      }
    case 'today':
      const computedStart = new Date()
      computedStart.setHours(0, 0, 0, 0)

      const computedEnd = new Date()
      computedEnd.setHours(23, 59, 59, 999)

      return {
        ...result,
        dateFrom: computedStart.toUTCString(),
        dateTo: computedEnd.toUTCString(),
      }
    case 'yesterday':
      const dateYesterday = subDays(DATE_TODAY, 1)

      return {
        ...result,
        dateFrom: startOfDay(dateYesterday),
        dateTo: endOfDay(dateYesterday),
      }
    case 'tomorrow':
      const dateTomorrow = addDays(DATE_TODAY, 1)

      return {
        ...result,
        dateFrom: startOfDay(dateTomorrow),
        dateTo: endOfDay(dateTomorrow),
      }
    case 'next_7_days':
      return {
        ...result,
        dateFrom: startOfDay(DATE_TODAY),
        dateTo: endOfDay(addDays(DATE_TODAY, 6)),
      }
    default:
      return {
        ...result,
        dateFrom: undefined,
        dateTo: undefined,
      }
  }
}

const resolveLabel: (option: DateRangeOption, t: TFunction) => string = (option, t) => {
  switch (option) {
    case '7_days':
      return t(`last_day_count_other`, { count: 7 })
    case 'last_3_month':
      return t(`last_month_count_other`, { count: 3 })
    case 'last_6_month':
      return t(`last_month_count_other`, { count: 6 })
    case 'custom':
      return t('custom_range_lbl')
    case 'lastyear':
      return t('last_year_lbl')
    case 'previousmonth':
      return t('previous_month_lbl')
    case 'this_month':
      return t('this_month_lbl')
    case 'this_quarter':
      return t('this_quarter_lbl')
    case 'previous_quarter':
      return t('previous_quarter_lbl')
    case 'thisyear':
      return t('this_year_lbl')
    case 'today':
      return t('today_lbl')
    case 'yesterday':
      return t('yesterday_lbl')
    case 'tomorrow':
      return t('tomorrow_lbl')
    case 'next_7_days':
      return t('next_day_count_other', { count: 7 })
    default:
      return t('all_lbl')
  }
}

const resolveTooltip: (option: DateRangeOption, t: TFunction) => string | undefined = (option, t) => {
  switch (option) {
    case '7_days':
      return t('date_range_tooltip_last_7_days')
    case 'next_7_days':
      return t('date_range_tooltip_next_7_days')
    case 'this_month':
      return t('date_range_tooltip_this_month')
    case 'this_quarter':
      return t('date_range_tooltip_this_quarter')
    case 'previous_quarter':
      return t('date_range_tooltip_previous_quarter')
    case 'thisyear':
      return t('date_range_tooltip_this_year')
    case 'lastyear':
      return t('date_range_tooltip_last_year')
    default:
      return undefined
  }
}

type Props = MenuProps &
  PopOverCustomMenuType & { updatePopupStyle?: ({ styleObj }: { styleObj: Record<string, string> }) => void }

export const DateRangeMenu: React.FC<Props> = ({
  dateFormat = 'dd/MM/yyyy',
  onChangeDisplayValue,
  onSubmit,
  onToggleOpenMenu,
  value,
  options = DEFAULT_OPTIONS,
  updatePopupStyle = () => {},
  trackId,
  maxDate,
  umamiEvent,
}) => {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState(value?.option ?? 'all')
  const [dates, setDates] = useState({
    dateFrom: value?.dateFrom ? new Date(value?.dateFrom) : DATE_TODAY,
    dateTo: value?.dateTo ? new Date(value?.dateTo) : DATE_TODAY,
  })
  const [error, setError] = useState<string | undefined>()
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    calcPopupPosition()
  }, [selectedOption])

  const label = resolveLabel(selectedOption, t)

  const calcPopupPosition = () => {
    if (!triggerRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()

    let popoverAdditionalStyle = {}
    if (isCustomOption(selectedOption)) {
      popoverAdditionalStyle = getPopOverStyle({ triggerRect })
    }
    updatePopupStyle({ styleObj: popoverAdditionalStyle })
  }

  const handleSubmit = () => {
    if (isCustomOption(selectedOption) && isAfter(dates.dateFrom, dates.dateTo)) {
      setError('Invalid date range')
      return
    }

    onToggleOpenMenu?.()
    onChangeDisplayValue?.(label)

    onSubmit(resolveDateRangeValue(dates.dateFrom, dates.dateTo, selectedOption))
  }

  const handleChange = (field: string) => (date: Date | [Date, Date]) => {
    if (!Array.isArray(date)) {
      setError(undefined)
      setDates({ ...dates, [field]: date })
    }
  }

  const fieldProps = (field: 'dateFrom' | 'dateTo') => ({
    datePicker: {
      closeOnSelect: false,
      dateFormat,
      endDate: dates.dateTo,
      inline: true,
      maxDate,
      name: field,
      onChange: handleChange(field),
      pickerSize: 'small' as const,
      selectsStart: field === 'dateFrom',
      selectsEnd: field === 'dateTo',
      startDate: dates.dateFrom,
      value: String(dates[field]),
    },
    textInput: {
      error,
      icon: ['far', 'calendar-alt'],
      marginBottom: '24px',
      onChange: () => {},
      value: format(new Date(dates[field]), dateFormat),
    },
  })

  const dateFromProps = fieldProps('dateFrom')
  const dateToProps = fieldProps('dateTo')

  return (
    <Container>
      <Box display="flex" flexDirection="column" justifyContent="space-between" ref={triggerRef}>
        <Box padding="10px" overflowY="auto">
          {options.map((option, index) => {
            const tooltip = resolveTooltip(option, t)
            const pill = (
              <Pill
                isHighlighted={option === selectedOption}
                data-umami-event={umamiEvent}
                data-umami-event-value={resolveLabel(option, t)}
                onClick={() => {
                  if (selectedOption === option) return
                  if (isCustomOption(option)) {
                    setDates({ dateFrom: new Date(), dateTo: new Date() })
                  } else {
                  }
                  setSelectedOption(option)
                }}
              >
                {resolveLabel(option, t)}
              </Pill>
            )

            return tooltip ? (
              <Tooltip key={index} position="right" title={tooltip}>
                {pill}
              </Tooltip>
            ) : (
              <React.Fragment key={index}>{pill}</React.Fragment>
            )
          })}
        </Box>
        <Box display="flex" alignContent="center" justifyContent="space-evenly" padding="0px 10px 10px">
          <Button
            marginX="4px"
            onClick={() => {
              if (typeof onToggleOpenMenu === 'function') onToggleOpenMenu()
            }}
            variant="ghost"
            width={100}
          >
            cancel_btn
          </Button>
          {trackId ? (
            <span data-analytic-track={trackId}>
              <Button marginX="4px" onClick={handleSubmit} width={100}>
                apply_btn
              </Button>
            </span>
          ) : (
            <Button marginX="4px" onClick={handleSubmit} width={100}>
              apply_btn
            </Button>
          )}
        </Box>
      </Box>
      {isCustomOption(selectedOption) && (
        <DateRangePanel>
          <DatePickerContainer>
            <TextInput {...dateFromProps.textInput} />
            <DatePicker {...dateFromProps.datePicker} />
          </DatePickerContainer>
          <DatePickerContainer>
            <TextInput {...dateToProps.textInput} />
            <DatePicker {...dateToProps.datePicker} />
          </DatePickerContainer>
        </DateRangePanel>
      )}
    </Container>
  )
}

const DateFilter: React.FC<FilterProps> = ({
  title,
  width = 'auto',
  placement,
  withUpdatedStyleProps = true,
  umamiEvent,
  ...props
}) => {
  const { t } = useTranslation()
  const [style, setStyles] = useState<Record<string, string>>({})
  const [displayValue, setDisplayValue] = useState<undefined | string>(() =>
    resolveLabel(props.value?.option ?? 'all', t),
  )

  const updatePopupStyle = ({ styleObj }: { styleObj: Record<string, string> }) => {
    if (withUpdatedStyleProps) setStyles(styleObj)
  }

  const popOverProps = {
    openIcon: ['far', 'calendar-alt'],
    closeIcon: ['far', 'calendar-alt'],
    customMenuList: (
      <DateRangeMenu
        updatePopupStyle={updatePopupStyle}
        {...props}
        umamiEvent={umamiEvent}
        onChangeDisplayValue={setDisplayValue}
      />
    ),
    displayValue,
    title,
    width,
    placement,
    style,
    isPortaled: props.isPortaled,
    umamiEvent,
  }

  useEffect(() => {
    let abort = false

    if (!abort) setDisplayValue(resolveLabel(props.value?.option ?? 'all', t))

    return () => {
      abort = true
    }
  }, [props.value, t])

  return <PopOver {...popOverProps} maxWidth={'70vw'} />
}

const Container = styled.div`
  display: flex;
  max-height: 43vh;
  max-width: 100vw;
  overflow: auto;
`

const DateRangePanel = styled.div`
  --dt-gap: 10px;
  --dt-pad: 15px;

  box-sizing: border-box;
  display: flex;
  gap: var(--dt-gap);
  padding: var(--dt-pad);
  border-left: 1px solid ${(p) => p.theme.colors.gray3};
`

const DatePickerContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
`

const Pill = styled.div<{ isHighlighted?: boolean }>`
  ${(p) => p.theme.font({ size: 14, weight: 'medium' })}
  background-color: ${(p) => (p.isHighlighted ? p.theme.colors.blue3 : p.theme.colors.white)};
  border: 1px solid ${(p) => p.theme.colors.defaultBorder};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${(p) => (p.isHighlighted ? p.theme.colors.white : p.theme.colors.gray5)};
  cursor: pointer;
  min-height: 30px;
  line-height: normal;
  margin-bottom: 8px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  padding: 0 8px;

  &:active {
    background-color: ${(p) => p.theme.colors.blue3};
    border-color: ${(p) => p.theme.colors.blue3};
    color: ${(p) => p.theme.colors.white};
  }

  ${(p) =>
    !p.isHighlighted &&
    css`
      &:disabled {
        background-color: ${(p) => p.theme.colors.gray3};
        border-color: ${(p) => p.theme.colors.gray4};
      }

      &:hover {
        background-color: ${(p) => p.theme.colors.gray1};
        border-color: ${(p) => p.theme.colors.gray5};

        &:active {
          background-color: ${(p) => p.theme.colors.blue3};
          color: ${(p) => p.theme.colors.white};
        }
      }
    `}
`

export default DateFilter
